import { useState } from 'react';
import { SubTypeOption } from '../../../../api/gms/group-category/group-category-api-state';

type SelectionData = {
    id?: number | string | null;
    name?: string | null;
};

interface Props {
    isShow: boolean;
    currentGroupClassification?: SelectionData | null;
    proposedGroupClassification?: SelectionData | null;
    currentOtherSpecifyGC?: string | null;
    proposedOtherSpecifyGC?: string | null;
    currentTypeOfGroup?: SelectionData | null;
    proposedTypeOfGroup?: SelectionData | null;
    currentOtherSpecifyToG?: string | null;
    proposedOtherSpecifyToG?: string | null;
    currentSubType?: SubTypeOption[];
    proposedSubType?: SubTypeOption[];
    currentAgeProfile?: string | null;
    proposedAgeProfile?: string | null;
    currentPaymentMode?: SelectionData | null;
    proposedPaymentMode?: SelectionData | null;
}

type ChangeItem = {
    key: string;
    label: string;
    current: string;
    proposed: string;
    currentSubTypes?: string[];
    proposedSubTypes?: string[];
};

const normalizeValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value).trim();
};

const getSubTypeNames = ( value?: SubTypeOption[] ): string[] => {
    if (!value || value.length === 0) {
        return [];
    }

    return value
        .map((item) => normalizeValue(item.name))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
};

const normalizeArray = ( value?: SubTypeOption[] ): string => {
    return getSubTypeNames(value).join(', ');
};

const buildGroupClassificationValue = ( classification?: SelectionData | null, otherSpecify?: string | null ): string => {
    const values: string[] = [];

    const name = normalizeValue(classification?.name);

    if (name) {
        values.push(name);
    }

    const other = normalizeValue(otherSpecify);

    if (other) {
        values.push(other);
    }

    return values.join(' • ');
};

const buildTypeOfGroupBaseValue = (typeOfGroup?: SelectionData | null, otherSpecify?: string | null): string => {
    const values: string[] = [];

    const name = normalizeValue(typeOfGroup?.name);

    if (name) {
        values.push(name);
    }

    const other = normalizeValue(otherSpecify);

    if (other) {
        values.push(other);
    }

    return values.join(' • ');
};

const buildTypeOfGroupCompareValue = (typeOfGroup?: SelectionData | null, otherSpecify?: string | null, subTypes?: SubTypeOption[]): string => {
    const baseValue = buildTypeOfGroupBaseValue(
        typeOfGroup,
        otherSpecify,
    );

    const subTypeValue = normalizeArray(subTypes);

    return [baseValue, subTypeValue]
        .filter(Boolean)
        .join(' • ');
};

const getChanges = ({
    currentGroupClassification,
    proposedGroupClassification,
    currentOtherSpecifyGC,
    proposedOtherSpecifyGC,
    currentTypeOfGroup,
    proposedTypeOfGroup,
    currentOtherSpecifyToG,
    proposedOtherSpecifyToG,
    currentSubType,
    proposedSubType,
    currentAgeProfile,
    proposedAgeProfile,
    currentPaymentMode,
    proposedPaymentMode,
}: Props): ChangeItem[] => {

    const changes: ChangeItem[] = [];
    const currentGroupClassificationValue = buildGroupClassificationValue(currentGroupClassification, currentOtherSpecifyGC,);
    const proposedGroupClassificationValue = buildGroupClassificationValue(proposedGroupClassification, proposedOtherSpecifyGC,);

    if (normalizeValue(currentGroupClassificationValue) !== normalizeValue(proposedGroupClassificationValue)) {
        changes.push({
            key: 'group-classification',
            label: 'Group Classification',
            current: currentGroupClassificationValue,
            proposed: proposedGroupClassificationValue,
        });
    }

    const currentTypeOfGroupValue = buildTypeOfGroupCompareValue(
        currentTypeOfGroup,
        currentOtherSpecifyToG,
        currentSubType,
    );

    const proposedTypeOfGroupValue = buildTypeOfGroupCompareValue(
        proposedTypeOfGroup,
        proposedOtherSpecifyToG,
        proposedSubType,
    );

    if (normalizeValue(currentTypeOfGroupValue) !== normalizeValue(proposedTypeOfGroupValue)) {
        changes.push({
            key: 'type-of-group',
            label: 'Type of Group',
            current: buildTypeOfGroupBaseValue(
                currentTypeOfGroup,
                currentOtherSpecifyToG,
            ),
            proposed: buildTypeOfGroupBaseValue(
                proposedTypeOfGroup,
                proposedOtherSpecifyToG,
            ),
            currentSubTypes: getSubTypeNames(currentSubType),
            proposedSubTypes: getSubTypeNames(proposedSubType),
        });
    }

    const currentAgeValue = normalizeValue(currentAgeProfile);
    const proposedAgeValue = normalizeValue(proposedAgeProfile);

    if (currentAgeValue !== proposedAgeValue) {
        changes.push({
            key: 'age-profile',
            label: 'Age Profile',
            current: currentAgeValue,
            proposed: proposedAgeValue,
        });
    }

    const currentPaymentValue = normalizeValue(currentPaymentMode?.name);
    const proposedPaymentValue = normalizeValue(proposedPaymentMode?.name);

    if (currentPaymentValue !== proposedPaymentValue) {
        changes.push({
            key: 'payment-mode',
            label: 'Mode of Payment',
            current: currentPaymentValue,
            proposed: proposedPaymentValue,
        });
    }

    return changes;
};

const NoChanges = () => {
    return (
        <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-3 text-[11px] text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true" >
                <path d="m5 12.5 4.5 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>No changes</span>
        </div>
    );
};

const SubTypeList = ({items, proposed = false}: {items?: string[]; proposed?: boolean}) => {
    
    if (!items || items.length === 0) {
        return null;
    }

    return (
        <div className="mt-1.5 ml-3 space-y-1">
            {items.map((item, index) => (
                <div key={`${item}-${index}`} className="flex items-start gap-1.5 text-[10px] leading-4 text-gray-500" >
                    <span className={`mt-[6px] h-1 w-1 shrink-0 rounded-full ${proposed ? 'bg-[#186cb2]' : 'bg-gray-400'}`} />
                    <span className="break-words">{item}</span>
                </div>
            ))}
        </div>
    );
};

const ChangeValue = ({label, value, proposed = false, subTypes}: {label: string; value: string; proposed?: boolean; subTypes?: string[]}) => {
    return (
        <div className="min-w-0 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
            <div className={`mb-1 text-[9px] font-semibold uppercase tracking-wide ${proposed ? 'text-[#186cb2]' : 'text-gray-400'}`}>
                {label}
            </div>
            <div className="break-words text-[11px] leading-relaxed text-gray-700">
                {value || 'Not provided'}
            </div>
            <SubTypeList items={subTypes} proposed={proposed} />
        </div>
    );
};

const ChangeItemView = ({item}: {item: ChangeItem}) => {
    return (
        <div className="grid grid-cols-1 gap-3 border-b border-gray-100 py-3 last:border-b-0 sm:grid-cols-2">
            <ChangeValue
                label={item.label}
                value={item.current}
                subTypes={item.currentSubTypes}
            />
            <div className="min-w-0 rounded-md bg-[#eef6fc] px-2.5 py-2">
                <ChangeValue
                    label={item.label}
                    value={item.proposed}
                    proposed
                    subTypes={item.proposedSubTypes}
                />
            </div>
        </div>
    );
};

export default function ProposedAmendGroupProfile(props: Props,) {

    const [showContent, setShowContent] = useState(true);
    const changes = getChanges(props);

    if (!props.isShow) {
        return null;
    }

    if (changes.length === 0) {
        return null;
    }

    return (
        <div className="grid grid-cols-1 gap-3.5">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                {/* ================================================= */}
                {/* SECTION HEADER */}
                {/* ================================================= */}
                <button
                    type="button"
                    onClick={() => setShowContent((previous) =>!previous)}
                    className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                    <div className="flex min-w-0 items-center gap-2">
                        <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 shrink-0 text-[#186cb2] transition-transform ${showContent ? 'rotate-0' : '-rotate-90'}`} aria-hidden="true" >
                            <path d="m7 10 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                            Group Profile & Payment Details
                        </h3>
                        <span className="rounded-full bg-[#e3f1fa] px-1.5 py-0.5 text-[8px] font-bold text-[#186cb2] ring-1 ring-[#c7e0f3]">
                            {changes.length}
                        </span>
                    </div>
                    <span className="shrink-0 text-[9px] font-medium text-slate-400">
                        {showContent ? 'Hide' : 'Show'}
                    </span>
                </button>
                {showContent && (
                    <>
                        {/* ================================================= */}
                        {/* CURRENT / PROPOSED HEADER */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 border-b border-slate-200 sm:grid-cols-2">
                            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:border-b-0 sm:border-r">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Current
                                </h3>
                            </div>
                            <div className="bg-[#f5f9fc] px-4 py-2.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[#186cb2]">
                                    Proposed
                                </h3>
                            </div>
                        </div>
                        {/* ================================================= */}
                        {/* CONTENT */}
                        {/* ================================================= */}
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                            {/* CURRENT */}
                            <div className="min-w-0 border-b border-slate-200 p-4 sm:border-b-0 sm:border-r">
                                <div className="space-y-3">
                                    {changes.map((item) => (
                                        <ChangeValue
                                            key={`current-${item.key}`}
                                            label={item.label}
                                            value={item.current}
                                            subTypes={item.currentSubTypes}
                                        />
                                    ))}
                                </div>
                            </div>
                            {/* PROPOSED */}
                            <div className="min-w-0 p-4">
                                <div className="space-y-3">
                                    {changes.map((item) => (
                                        <ChangeValue
                                            key={`proposed-${item.key}`}
                                            label={item.label}
                                            value={item.proposed}
                                            proposed
                                            subTypes={item.proposedSubTypes}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}